
import React, { useState } from 'react'
import { X, Plus, Trash2 } from 'lucide-react'
import { useCompras } from '../hooks/useCompras'

interface CotacaoLoteFormProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (requisicaoIds: string[], cotacoes: any[]) => Promise<void>
  requisicoesSelecionadas: any[]
}

interface Cotacao {
  fornecedorId: string
  nomeFornecedor: string
  precoUnitario: number
  prazoEntrega: number
  condicoesPagamento: string
  observacoes: string
}

const CotacaoLoteForm: React.FC<CotacaoLoteFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  requisicoesSelecionadas
}) => {
  const { fornecedores } = useCompras()
  const [cotacoes, setCotacoes] = useState<Cotacao[]>([])

  // Add new cotacao
  const addCotacao = () => {
    if (cotacoes.length >= 3) {
      alert('Máximo de 3 fornecedores por cotação')
      return
    }

    const newCotacao: Cotacao = {
      fornecedorId: '',
      nomeFornecedor: '',
      precoUnitario: 0,
      prazoEntrega: 0,
      condicoesPagamento: '',
      observacoes: ''
    }
    setCotacoes([...cotacoes, newCotacao])
  }

  // Update cotacao
  const updateCotacao = (index: number, field: keyof Cotacao, value: any) => {
    const newCotacoes = [...cotacoes]
    newCotacoes[index] = { ...newCotacoes[index], [field]: value }
    
    // If selecting a fornecedor, update the name
    if (field === 'fornecedorId') {
      const fornecedor = fornecedores.find(f => f._id === value)
      if (fornecedor) {
        newCotacoes[index].nomeFornecedor = fornecedor.nome
      }
    }
    
    setCotacoes(newCotacoes)
  }

  // Remove cotacao
  const removeCotacao = (index: number) => {
    setCotacoes(cotacoes.filter((_, i) => i !== index))
  }

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (cotacoes.length === 0) {
      alert('Adicione pelo menos uma cotação')
      return
    }

    if (cotacoes.some(c => !c.fornecedorId || !c.precoUnitario)) {
      alert('Preencha todos os campos obrigatórios das cotações')
      return
    }

    const requisicaoIds = requisicoesSelecionadas.map(r => r._id)
    await onSubmit(requisicaoIds, cotacoes)
    onClose()
    setCotacoes([])
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">
              Cotação em Lote ({requisicoesSelecionadas.length} requisições)
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Requisições Selecionadas */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">Requisições Selecionadas</h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              {requisicoesSelecionadas.map(req => (
                <div key={req._id} className="flex justify-between items-center py-2 border-b last:border-b-0">
                  <div>
                    <span className="font-medium">{req.numero}</span>
                    <span className="text-gray-600 ml-2">{req.nomeInsumo}</span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {req.quantidade} {req.unidadeMedida}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Cotações */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Cotações (máximo 3 fornecedores)</h3>
                <button
                  type="button"
                  onClick={addCotacao}
                  disabled={cotacoes.length >= 3}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus size={16} />
                  Adicionar Fornecedor
                </button>
              </div>

              <div className="space-y-4">
                {cotacoes.map((cotacao, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg border">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-medium">Fornecedor {index + 1}</h4>
                      <button
                        type="button"
                        onClick={() => removeCotacao(index)}
                        className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Fornecedor *
                        </label>
                        <select
                          value={cotacao.fornecedorId}
                          onChange={(e) => updateCotacao(index, 'fornecedorId', e.target.value)}
                          required
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Selecionar fornecedor...</option>
                          {fornecedores.filter(f => f.ativo && !f.bloqueado).map(fornecedor => (
                            <option key={fornecedor._id} value={fornecedor._id}>
                              {fornecedor.nome}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Preço Unitário *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={cotacao.precoUnitario}
                          onChange={(e) => updateCotacao(index, 'precoUnitario', Number(e.target.value))}
                          required
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Prazo de Entrega (dias) *
                        </label>
                        <input
                          type="number"
                          value={cotacao.prazoEntrega}
                          onChange={(e) => updateCotacao(index, 'prazoEntrega', Number(e.target.value))}
                          required
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Condições de Pagamento *
                        </label>
                        <input
                          type="text"
                          value={cotacao.condicoesPagamento}
                          onChange={(e) => updateCotacao(index, 'condicoesPagamento', e.target.value)}
                          placeholder="Ex: 30 dias, À vista"
                          required
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div className="md:col-span-2 lg:col-span-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Observações
                        </label>
                        <textarea
                          value={cotacao.observacoes}
                          onChange={(e) => updateCotacao(index, 'observacoes', e.target.value)}
                          rows={2}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                {cotacoes.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    Nenhuma cotação adicionada. Clique em "Adicionar Fornecedor" para começar.
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-4 pt-6 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Salvar Cotações
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default CotacaoLoteForm
